import {NodeType} from "crqlar-react-common/dist/models/automation/automation";

export interface NodeCopy {
    summary: string;
    detail: string;
    /** Replaces `detail` in the config dialog only: one sentence, for an editor that asks something right away. */
    dialogLine?: string;
}

export const NODE_COPY: Partial<Record<NodeType, NodeCopy>> = {
    [NodeType.MANUAL_TRIGGER]: {
        summary: 'Starts only when someone runs the flow by hand.',
        detail: 'Start a run from this node with Run for everyone. Other triggers in the automation keep their own schedules and events.',
    },
    [NodeType.SCHEDULED_TRIGGER]: {
        summary: 'Starts on a schedule you set.',
        detail: 'Runs once at a chosen moment, or again every day, week or month. The schedule only fires while the automation is active.',
    },
    [NodeType.FORM_SUBMITTED]: {
        summary: 'Starts when a form is submitted.',
        detail: 'Fires every time a guest submits a form. Pick one form, or leave it open to react to all of them. The answers, and the email and phone sent with them, are available to the steps that follow.',
    },
    [NodeType.RESERVATION_CREATED]: {
        summary: 'Starts when a reservation is created.',
        detail: 'Fires for each new reservation, optionally only for the sources it may come from — the booking engine, the guest app, the dashboard, your PMS or the system itself — and only for one sector or service. The reservation, its dates and the guest profiles on it are available to the steps that follow.',
    },
    [NodeType.RESERVATION_STATUS_CHANGED]: {
        summary: 'Starts when a reservation reaches a status.',
        detail: 'Fires when a reservation moves into one of the statuses you pick, and optionally also when it is created directly in one of them. The same source, sector and service filters as the created trigger apply. The reservation, the status it came from and the one it reached are available to the steps that follow.',
    },
    [NodeType.SURVEY_SUBMITTED]: {
        summary: 'Starts when a survey response comes in.',
        detail: 'Fires once for every response from an invited guest to the surveys you pick, at least one. Narrow it further to guests in certain guest categories or to certain sectors. Responses given through an anonymous link never start it, because they carry no guest to write to. To act only on some responses, such as unhappy guests, add a Continue if step after it. The response, its scores and the guest are available to the steps that follow.',
    },
    [NodeType.SURVEY_REMINDER_DUE]: {
        summary: 'Starts when a survey invitation goes unanswered.',
        detail: 'Fires once for every invitation to the surveys you pick, at least one, that is still unanswered a set number of days after it was sent. The same invitation is never reminded twice, no matter how many flows are watching it. The survey link and the guest are available to the steps that follow.',
    },
    [NodeType.AUDIENCE]: {
        summary: 'Runs the following steps for every guest in an audience.',
        detail: 'Pick one of your saved audiences or build a new one right here. Every guest in it goes through the following steps, one run per guest, with that guest\'s profile — name, language, contact details, stay statistics — available to them.',
        dialogLine: 'Every guest in the audience goes through the steps below, one run per guest.',
    },
    [NodeType.TEAM_MEMBERS]: {
        summary: 'Runs the following steps for each person you pick.',
        detail: 'Picks people in your organisation by user type, by the apps they can open or by name. Anyone who matches one of them goes through the following steps, one run per person.',
    },
    [NodeType.SEND_EMAIL]: {
        summary: 'Sends an email to the chosen recipients.',
        detail: 'Sends either an email you compose here or a stored template. The recipient comes from a profile id, from a plain address, or from the guest the run is for when an Audience step comes earlier in the flow.',
        dialogLine: 'One email per run, to whoever this run is for or to an address you enter.',
    },
    [NodeType.SEND_PUSH]: {
        summary: 'Sends a push notification to the guest app.',
        detail: 'Delivers a short message to the guest app of the selected profiles. Only profiles with the app installed and push notifications allowed receive it.',
    },
    [NodeType.SURVEY_INVITE]: {
        summary: 'Creates the guest’s personal link to a survey.',
        detail: 'Creates the guest’s personal survey link, an invitation, for the reservation or guest the run is about. The link is available to the steps that follow, usually a Send Email step below that puts it into the message. An invitation to the same survey for the same reservation is reused rather than issued again.',
    },
    [NodeType.SURVEY_REPORT]: {
        summary: 'Builds a survey report as PDF, Word or Excel files.',
        detail: 'Builds the evaluation of one survey for a recent period, once per run, as PDF, Word or Excel files. Put it before a Team members step: a Send team email step below can attach the files and use the survey name, the period, the responses and the scores in its text.',
    },
    [NodeType.SEND_TEAM_EMAIL]: {
        summary: 'Emails each team member using a builder template and selected attachments.',
        detail: 'Sends one email to the team member each run is for, so it goes below a Team members step. Design the email in the builder, insert values and dashboard links from earlier steps, and select the steps whose files must be attached.',
        dialogLine: 'One email per team member, designed in the builder, with files from earlier steps.',
    },
    [NodeType.FULL_SYNC_AUDIENCE]: {
        summary: 'Refreshes all active audiences and syncs with Maileon when connected.',
        detail: 'Refreshes audience sizes across your organisation and starts a Maileon sync when an account is connected. This maintenance step always covers all active audiences.',
    },
    [NodeType.CONTINUE_IF]: {
        summary: 'Continues only when the conditions match.',
        detail: 'Choose conditions from earlier steps. Matching guests continue; everyone else stops here.',
    },
    [NodeType.IF_CONDITION]: {
        summary: 'Splits the flow into a Yes and a No branch.',
        detail: 'Evaluates a condition and carries on down the branch that matches: the green Yes branch under the left of the card when the condition is met, the red No branch under the right of it when it is not. A condition can be a plain field comparison or a question answered by AI.',
        dialogLine: 'Compares values from earlier steps, or asks AI, then sends each run down the matching branch.',
    },
    [NodeType.WAIT]: {
        summary: 'Pauses the flow for a set time.',
        detail: 'Holds the run until the wait is over, then continues with the next step. The run stays open while it waits, so a flow can span hours or days.',
    },
    [NodeType.FINISH]: {
        summary: 'Stops the run here.',
        detail: 'A true end: nothing can be connected below it. Put it where a branch is meant to stop — the No side of an If, for instance — so the automation says so instead of leaving the branch open. In a fan-out it ends that member’s run.',
    },
    [NodeType.AFTER_EVERYONE]: {
        summary: 'Waits until all runs are done, then runs the steps below once.',
        detail: 'Put it after the last step that runs once per guest or team member. Each of those runs ends here; the automation waits until all of them have finished, then the steps below run a single time — a summary, an internal push. It knows how many runs were handled and how many failed.',
    },
};

export const outputNameSx = {
    backgroundColor: '#F4F0FF',
    borderRadius: '5px',
    color: '#6D28D9',
    fontFamily: '"Roboto Mono", "SFMono-Regular", Consolas, monospace',
    fontSize: '0.75rem',
    fontWeight: 700,
    lineHeight: 1.4,
    padding: '0.08em 0.4em',
    whiteSpace: 'nowrap',
} as const;
